import React from "react";
import { Link } from "react-router-dom";
import { Mail, Twitter, Instagram, Linkedin, Youtube } from "lucide-react";
import Logo from "./Logo";

export default function SiteFooter() {
  const year = new Date().getFullYear();
  return (
    <footer className="border-t border-zinc-200 bg-zinc-50/50 dark:border-zinc-800 dark:bg-zinc-950/50" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center gap-2">
              <Logo className="h-9 w-9" />
              <div>
                <div className="font-heading text-lg font-bold leading-none">Syllox</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Built for Aspirants</div>
              </div>
            </Link>
            <p className="mt-4 max-w-xs text-sm text-zinc-500">
              The study companion for India's NEET & JEE aspirants. Track, focus, conquer.
            </p>
          </div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-zinc-700 dark:text-zinc-300">Product</div>
            <ul className="mt-4 space-y-2 text-sm text-zinc-500">
              <li><Link to="/signup" className="hover:text-emerald-600" data-testid="footer-signup">Sign up</Link></li>
              <li><Link to="/login" className="hover:text-emerald-600" data-testid="footer-login">Login</Link></li>
              <li><Link to="/premium" className="hover:text-emerald-600">Premium</Link></li>
            </ul>
          </div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-zinc-700 dark:text-zinc-300">Company</div>
            <ul className="mt-4 space-y-2 text-sm text-zinc-500">
              <li><Link to="/about" className="hover:text-emerald-600" data-testid="footer-about">About us</Link></li>
              <li><Link to="/contact" className="hover:text-emerald-600" data-testid="footer-contact">Contact</Link></li>
              <li>
                <a href="mailto:contact@syllox.in" className="hover:text-emerald-600">contact@syllox.in</a>
              </li>
            </ul>
          </div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-zinc-700 dark:text-zinc-300">Legal</div>
            <ul className="mt-4 space-y-2 text-sm text-zinc-500">
              <li><Link to="/privacy" className="hover:text-emerald-600" data-testid="footer-privacy">Privacy policy</Link></li>
              <li><Link to="/terms" className="hover:text-emerald-600" data-testid="footer-terms">Terms of service</Link></li>
              <li><Link to="/delete-account" className="hover:text-emerald-600" data-testid="footer-delete-account">Delete account</Link></li>
              <li><Link to="/contact#support" className="hover:text-emerald-600">Support</Link></li>
            </ul>
          </div>
        </div>

        {/* Download NOW — app-store badges. Container is flex-wrap so a
            second badge (Play Store) can be dropped in beside this one
            without any layout rework. Badges stack cleanly on mobile. */}
        <div className="mt-10 border-t border-zinc-200 pt-8 dark:border-zinc-800" data-testid="footer-download-row">
          <div className="text-xs font-semibold uppercase tracking-wider text-zinc-700 dark:text-zinc-300">
            Download Now
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <a
              href="https://play.google.com/store/apps/details?id=com.syllox.myapp"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Get Syllox on Google Play"
              data-testid="footer-playstore-badge"
              className="inline-flex overflow-hidden rounded-lg transition-transform duration-200 hover:scale-[1.03]"
            >
              <img
                alt="Get it on Google Play"
                src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                className="h-12 w-auto object-contain"
                loading="lazy"
              />
            </a>
            <a
              href="https://indusapp.store/7z0y9nhl"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Get Syllox on Indus App Store"
              data-testid="footer-indus-badge"
              className="inline-flex overflow-hidden rounded-lg transition-transform duration-200 hover:scale-[1.03]"
            >
              <img
                alt="Get it on Indus Appstore"
                src="https://docstore.indusappstore.com/public/external/developerdashboard-static/badge-black-background-english.png"
                className="h-12 w-auto object-contain"
                loading="lazy"
              />
            </a>
          </div>
        </div>

        <div className="mt-10 flex flex-col items-start justify-between gap-4 border-t border-zinc-200 pt-6 sm:flex-row sm:items-center dark:border-zinc-800">
          <div className="text-xs text-zinc-500">© {year} Syllox · Built for Aspirants · Made with ❤️ in India</div>
          <div className="flex items-center gap-4 text-zinc-500" data-testid="footer-social-row">
            <span className="text-xs font-semibold uppercase tracking-wider text-zinc-600 dark:text-zinc-400">Follow us</span>
            <div className="flex items-center gap-3">
              <a href="mailto:contact@syllox.in" aria-label="Email Syllox" className="transition-all duration-200 hover:scale-110 hover:text-emerald-600"><Mail size={16}/></a>
              <a href="https://x.com/Sylloxin" target="_blank" rel="noopener noreferrer" aria-label="Syllox on X (Twitter)" className="transition-all duration-200 hover:scale-110 hover:text-emerald-600" data-testid="footer-x-link"><Twitter size={16}/></a>
              <a href="https://www.instagram.com/syllox.in?igsh=Y2tpaml5MDZmZHpw" target="_blank" rel="noopener noreferrer" aria-label="Syllox on Instagram" className="transition-all duration-200 hover:scale-110 hover:text-emerald-600" data-testid="footer-instagram-link"><Instagram size={16}/></a>
              <a href="https://www.linkedin.com/company/syllox/?viewAsMember=true" target="_blank" rel="noopener noreferrer" aria-label="Syllox on LinkedIn" className="transition-all duration-200 hover:scale-110 hover:text-emerald-600" data-testid="footer-linkedin-link"><Linkedin size={16}/></a>
              <a href="https://youtube.com/@sylloxin?si=YvcnbNL8DK8mQY6A" target="_blank" rel="noopener noreferrer" aria-label="Syllox on YouTube" className="transition-all duration-200 hover:scale-110 hover:text-emerald-600" data-testid="footer-youtube-link"><Youtube size={16}/></a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
